/**
 * AdminCxC — Dashboard de Cuentas por Cobrar a agencias.
 *
 * Refleja el "neto Entretur" que la agencia asumió contra su cupo de crédito
 * (reservas con forma_pago_agencia=credito + saldo_a_cargo_de=agencia).
 *
 * Sigue el mismo modelo/diseño que AdminComisiones (DashboardTab) — misma tabla
 * por agencia con drill-down a lista de reservas — con dos añadidos propios de
 * CxC: barra de utilización del cupo y columnas de cupo/utilizado/disponible.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, Chip, TextField, Card, CardContent, CircularProgress, Alert, Autocomplete,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  LinearProgress, Tooltip, MenuItem, FormControl, InputLabel, Select, IconButton,
  Tabs, Tab,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import BusinessIcon from '@mui/icons-material/Business';
import PendingIcon from '@mui/icons-material/Pending';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentIcon from '@mui/icons-material/Payment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import HistoryIcon from '@mui/icons-material/History';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ReservaFinancieroModal from './finanzas/ReservaFinancieroModal';
import { StyledDialog, inputStyles } from './common';
import ComprobanteBlock, { useComprobanteUploader } from './common/ComprobanteBlock';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return localStorage.getItem('empresa_activa') || user.company_id || user.empresa_id || '1';
  } catch { return '1'; }
}
// Consulta pura: el comercial ve la cartera de sus agencias pero no mueve
// plata. El backend lo bloquea igual con 403; esto evita ofrecerle un botón
// que solo lo llevaría a un error.
function esSoloConsulta() {
  try { return (JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase() === 'comercial'; }
  catch { return false; }
}

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('user_id');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    // X-User-Id: exigido por get_current_user() del backend en endpoints que
    // registran auditoría (ej. POST /turismo/finanzas/pagos-recibidos). Sin
    // este header cualquier POST devuelve 422 "Field required".
    ...(userId ? { 'X-User-Id': userId } : {}),
  };
}
function fmtMoney(n) {
  return '$' + new Intl.NumberFormat('es-CO', { minimumFractionDigits: 0 }).format(Number(n || 0));
}
function firstDayOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function today() {
  return new Date().toISOString().slice(0, 10);
}
function fmtFecha(iso) {
  if (!iso) return '—';
  try {
    const s = String(iso);
    // Cuando la fecha viene como YYYY-MM-DD plana (sin hora, sin timezone),
    // `new Date(s)` la interpreta como UTC medianoche → en Colombia (UTC-5)
    // se muestra como el día anterior. Parseamos manualmente para respetar
    // la fecha calendario del backend.
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      return d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    return new Date(s).toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso.slice(0, 10); }
}

const cardStyle = {
  borderRadius: 4,
  background: 'rgba(255, 255, 255, 0.9)',
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  overflow: 'hidden',
};

/** Color de la barra de utilización según el porcentaje consumido del cupo. */
function colorUtilizacion(pct) {
  if (pct == null) return '#94a3b8';
  if (pct >= 90) return '#dc2626';
  if (pct >= 70) return '#f59e0b';
  return '#10b981';
}


// Color según antigüedad de la deuda (semáforo 30/60/90 días).
function colorAntiguedad(dias) {
  if (dias == null) return '#94a3b8';
  if (dias >= 90) return '#7f1d1d';   // rojo muy oscuro
  if (dias >= 60) return '#dc2626';   // rojo
  if (dias >= 30) return '#f59e0b';   // amarillo
  return '#10b981';                    // verde
}

/** Mensaje de WhatsApp pre-formateado para recordatorio de deuda. */
function buildRecordatorioCxCMsg(a) {
  return [
    `Hola ${a.agencia_nombre} 👋`,
    ``,
    `Te escribo del área de finanzas de Entretur para recordarte tu saldo pendiente con nosotros:`,
    ``,
    `💰 Total pendiente: *$${Number(a.total_cxc_periodo || 0).toLocaleString('es-CO')}*`,
    `📋 Reservas involucradas: ${a.num_reservas}`,
    `⏱️ Antigüedad de la deuda más vieja: ${a.antiguedad_max_dias || 0} días`,
    ``,
    `Cuando puedas nos avisas para coordinar el pago. ¡Gracias! 🙌`,
  ].join('\n');
}

export default function AdminCxC() {
  // Tab 0 = "Deuda actual" (default) · Tab 1 = "CxC del período"
  const [tab, setTab] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Filtros comunes
  // La agencia puede venir en la dirección: el Estado de cuenta manda para
  // acá con `?agencia=`, para no obligar a buscarla otra vez en el filtro.
  const [searchParams] = useSearchParams();
  const [agenciaId, setAgenciaId] = useState(searchParams.get('agencia') || '');
  // Filtro específico "Deuda actual"
  const [diasMin, setDiasMin] = useState('');   // '' | '30' | '60' | '90'
  // Filtros específicos de la vista del período. Arrancan vacíos y se llenan
  // con el rango que abarca toda la deuda viva, que llega del servidor. Antes
  // arrancaban en "del 1 del mes a hoy", y con ese rango los números nunca
  // podían coincidir con los de la deuda actual: dejaban fuera lo de meses
  // anteriores y, sobre todo, los pasadías que todavía no han salido.
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const rangoPuesto = useRef(false);
  const [detalleAgencia, setDetalleAgencia] = useState(null);
  const empresaId = getEmpresaId();

  // Params comunes al dashboard y a los reportes de cartera. Comparten la
  // misma función para que el Excel y el PDF no puedan quedar mostrando un
  // universo distinto del que se ve en pantalla.
  const buildParams = useCallback(() => {
    const params = new URLSearchParams({ empresa_id: empresaId });
    if (tab === 1) {
      // "CxC del período": aplicar fechas
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
    } else {
      // "Deuda actual": sin fechas + filtro antigüedad
      if (diasMin) params.append('dias_min', diasMin);
    }
    if (agenciaId) params.append('agencia_id', agenciaId);
    return params;
  }, [empresaId, tab, agenciaId, diasMin, fechaDesde, fechaHasta]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/turismo/cxc/dashboard?${buildParams()}`, { headers: getAuthHeaders() });
      setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [buildParams]);

  // formato = 'excel' | 'pdf'. El navegador dispara la descarga por el header
  // Content-Disposition que manda el backend.
  const descargarCartera = (formato) => {
    window.open(`${API_BASE}/turismo/cxc/export/${formato}?${buildParams()}`, '_blank');
  };

  useEffect(() => { cargar(); }, [cargar]);

  // Catálogo de agencias con CxC (para el dropdown de filtro). Se carga una vez.
  const [agenciasCat, setAgenciasCat] = useState([]);
  useEffect(() => {
    fetch(`${API_BASE}/turismo/cxc/dashboard?empresa_id=${empresaId}`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => {
        setAgenciasCat(
          (d.agencias || [])
            .map(a => ({ id: a.agencia_id, nombre: a.agencia_nombre }))
            .sort((a, b) => a.nombre.localeCompare(b.nombre))
        );
        // Solo la primera vez: después manda lo que el usuario haya elegido.
        const r = d.rango_deuda_viva || {};
        if (!rangoPuesto.current && (r.desde || r.hasta)) {
          rangoPuesto.current = true;
          setFechaDesde(r.desde || firstDayOfMonth());
          setFechaHasta(r.hasta || today());
        }
      })
      .catch(() => {});
  }, [empresaId]);

  const resumen = data?.resumen_global || {};
  const esDeudaActual = tab === 0;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ color: '#134e4a', fontWeight: 700, fontSize: 20, mb: 0.5 }}>
          Cuentas por Cobrar (CxC)
        </Typography>
        <Typography sx={{ color: '#64748b', fontSize: 13 }}>
          {esDeudaActual
            ? 'Quién debe HOY, sin importar cuándo se generó la CxC. Filtra por agencia o por antigüedad.'
            : 'Qué se generó, qué se pagó y qué queda, de los pasadías de ese rango. '
              + 'Abre con el rango que cubre toda la deuda viva, así el pendiente '
              + 'coincide con la deuda actual. Si achicas el rango, deja de coincidir.'}
        </Typography>
      </Box>

      {/* Tabs internos */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: '1px solid #e2e8f0', mb: 2,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: 13 },
              '& .Mui-selected': { color: '#0891b2' },
              '& .MuiTabs-indicator': { bgcolor: '#0891b2', height: 3, borderRadius: 2 } }}>
        <Tab label="🔴 Deuda actual" />
        <Tab label="📅 Historia del período" />
      </Tabs>

      {/* Filtros */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Buscador en vez de desplegable: son ~190 agencias y bajar por la
            lista era inviable. Mismo componente que en Comisiones por pagar. */}
        <Autocomplete
          size="small"
          sx={{ minWidth: 260 }}
          options={agenciasCat}
          getOptionLabel={(o) => o?.nombre || ''}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          value={!agenciaId
            ? null
            : (agenciasCat.find(a => String(a.id) === String(agenciaId)) || null)}
          onChange={(_, opcion) => setAgenciaId(opcion ? opcion.id : '')}
          // Prioriza las que EMPIEZAN por lo escrito, sin descartar las que lo
          // contienen: muchos nombres arrancan con una palabra genérica y
          // quien busca "brahiam" no debería perderse "Agencia Brahiam".
          filterOptions={(opciones, { inputValue }) => {
            const q = (inputValue || '').trim().toLowerCase();
            if (!q) return opciones;
            const empiezan = [], contienen = [];
            for (const o of opciones) {
              const n = (o.nombre || '').toLowerCase();
              if (n.startsWith(q)) empiezan.push(o);
              else if (n.includes(q)) contienen.push(o);
            }
            return [...empiezan, ...contienen];
          }}
          noOptionsText="Ninguna agencia coincide"
          clearOnEscape
          renderInput={(params) => (
            <TextField {...params} label="Agencia" placeholder="Todas — escribe para buscar" />
          )}
        />

        {esDeudaActual ? (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Antigüedad</InputLabel>
            <Select label="Antigüedad" value={diasMin}
              onChange={e => setDiasMin(e.target.value)}>
              <MenuItem value="">Toda</MenuItem>
              <MenuItem value="30">Más de 30 días</MenuItem>
              <MenuItem value="60">Más de 60 días</MenuItem>
              <MenuItem value="90">Más de 90 días</MenuItem>
            </Select>
          </FormControl>
        ) : (
          <>
            <TextField sx={{ ...inputStyles, minWidth: 160 }} type="date" label="Desde"
              InputLabelProps={{ shrink: true }} value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
            <TextField sx={{ ...inputStyles, minWidth: 160 }} type="date" label="Hasta"
              InputLabelProps={{ shrink: true }} value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
          </>
        )}

        <Button size="small" variant="outlined" onClick={() => {
          setAgenciaId(''); setDiasMin('');
          setFechaDesde(firstDayOfMonth()); setFechaHasta(today());
        }} sx={{ textTransform: 'none' }}>
          Limpiar
        </Button>

        {/* Reporte de cartera. Va a la derecha de los filtros porque lo que
            descarga es exactamente lo que los filtros dejaron en pantalla. */}
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Tooltip title="Reporte de cartera en Excel: resumen por agencia, detalle por reserva y antigüedad" arrow>
            <span>
              <Button size="small" variant="outlined" startIcon={<TableChartIcon />}
                disabled={loading || !data?.agencias?.length}
                onClick={() => descargarCartera('excel')}
                sx={{ textTransform: 'none', color: '#15803d', borderColor: '#bbf7d0',
                      '&:hover': { borderColor: '#15803d', bgcolor: '#f0fdf4' } }}>
                Excel
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Mismo reporte en PDF, listo para imprimir o enviar" arrow>
            <span>
              <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />}
                disabled={loading || !data?.agencias?.length}
                onClick={() => descargarCartera('pdf')}
                sx={{ textTransform: 'none', color: '#b91c1c', borderColor: '#fecaca',
                      '&:hover': { borderColor: '#b91c1c', bgcolor: '#fef2f2' } }}>
                PDF
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* KPIs — mismo patrón que "Venta diaria" en Comercial y que
              Comisiones por pagar: un Card contenedor con rejilla de tiles
              tintados. Valores completos, no compactados: hay ancho de sobra
              y "$5.670.000" se lee de una vez. */}
          <Card sx={{ borderRadius: 2, mb: 3 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center',
                         justifyContent: 'space-between', mb: 1.5, gap: 1 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>
                  {esDeudaActual ? 'Deuda viva' : 'CxC del período'}
                  {data?.total_agencias != null &&
                    ` · ${data.total_agencias} agencia${data.total_agencias === 1 ? '' : 's'}`}
                </Typography>
                {esDeudaActual && (resumen.antiguedad_max_global || 0) > 0 && (
                  <Chip size="small"
                    label={`hasta ${resumen.antiguedad_max_global} días`}
                    sx={{ bgcolor: `${colorAntiguedad(resumen.antiguedad_max_global)}22`,
                          color: colorAntiguedad(resumen.antiguedad_max_global),
                          fontWeight: 800, fontSize: 12 }} />
                )}
              </Box>
              <Box sx={{ display: 'grid',
                         gridTemplateColumns: esDeudaActual
                           ? { xs: 'repeat(2, minmax(0,1fr))',
                               sm: 'repeat(3, minmax(0,1fr))',
                               lg: 'repeat(4, minmax(0,1fr))',
                               xl: 'repeat(7, minmax(0,1fr))' }
                           : { xs: 'repeat(2, minmax(0,1fr))',
                               md: 'repeat(4, minmax(0,1fr))' },
                         gridAutoRows: '1fr', alignItems: 'stretch', gap: 1 }}>
                {esDeudaActual ? (
                  <>
                    {/* La deuda se parte según dónde cae el pasadía respecto
                        a hoy: lo vencido ya es exigible, lo futuro es crédito
                        en curso. Mezclarlos hacía ver como mora algo que
                        todavía no lo es. Los tres suman el total. */}
                    <TileCxC label="Vencido" v={resumen.cxc_vencido} color="#b91c1c" bg="#fee2e2" />
                    <TileCxC label="Vence hoy" v={resumen.cxc_vence_hoy} color="#a16207" bg="#fef3c7" />
                    <TileCxC label="Futuro (aún no viaja)" v={resumen.cxc_futuro} color="#475569" bg="#f1f5f9" />
                    <TileCxC label="Total deuda viva" v={resumen.total_cxc_periodo} color="#0f766e" bg="#ccfbf1" />
                    {/* La misma cuenta al revés: plata que las agencias
                        transfirieron de más y todavía no se aplica a ninguna
                        reserva. Solo se dibuja si existe. */}
                    {resumen.total_saldo_a_favor > 0 && (
                      <TileCxC label="A favor de agencias" v={resumen.total_saldo_a_favor}
                               color="#15803d" bg="#dcfce7" />
                    )}
                    <TileCxC label="Agencias que deben" v={data?.total_agencias || 0} color="#0e7490" bg="#cffafe" conteo />
                    <TileCxC label="Reservas pendientes" v={resumen.num_reservas || 0} color="#7c3aed" bg="#ede9fe" conteo />
                    <TileCxC label="Deuda más vieja" v={`${resumen.antiguedad_max_global || 0} días`}
                             color={colorAntiguedad(resumen.antiguedad_max_global)} bg="#f8fafc" texto />
                  </>
                ) : (
                  <>
                    {/* Los tres primeros cuentan la historia y cuadran entre
                        sí: generado = pagado + pendiente. Antes solo se veía
                        el pendiente, y el número no se parecía a lo que la
                        gente esperaba de una vista "del período". */}
                    <TileCxC label="CxC generada en el período" v={resumen.total_cxc_generado} color="#0e7490" bg="#cffafe" />
                    <TileCxC label="Ya pagado" v={resumen.total_pagado} color="#166534" bg="#dcfce7" />
                    <TileCxC label="Pendiente de ese período" v={resumen.total_cxc_periodo} color="#b91c1c" bg="#fee2e2" />
                    <TileCxC label="Reservas CxC" v={resumen.num_reservas || 0} color="#a16207" bg="#fef3c7" conteo />
                    <TileCxC label="Ya saldadas" v={resumen.reservas_saldadas || 0} color="#166534" bg="#dcfce7" conteo />
                    <TileCxC label="Facturado del período" v={resumen.total_facturado} color="#0f766e" bg="#ccfbf1" />
                  </>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Tabla por agencia */}
          {(!data?.agencias || data.agencias.length === 0) ? (
            <Box sx={{ ...cardStyle, p: 5, textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 60, color: '#10b981', mb: 1.5 }} />
              <Typography sx={{ color: '#134e4a', fontSize: 16, fontWeight: 600 }}>
                {esDeudaActual ? 'Ninguna agencia tiene deuda pendiente' : 'No se generaron CxC en este período'}
              </Typography>
              <Typography sx={{ color: '#64748b', fontSize: 12, mt: 0.5 }}>
                {esDeudaActual
                  ? 'Todas las agencias están al día. Ajusta el filtro si buscabas antigüedad específica.'
                  : `Ninguna reserva con crédito de agencia entre ${fmtFecha(fechaDesde)} y ${fmtFecha(fechaHasta)}.`}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={cardStyle}>
              <Table size="small" sx={{
                  // 12 columnas: sin apretar el espaciado la tabla se corta
                  // y la columna Acciones queda fuera de vista.
                  '& td, & th': { px: 0.75, py: 0.75, fontSize: 12 },
                  '& th': { fontSize: 11, whiteSpace: 'nowrap' },
                }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Agencia</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="center">Res.</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">
                      {esDeudaActual ? 'Deuda actual' : 'CxC del período'}
                    </TableCell>
                    {/* Desglose por exigibilidad, mismo criterio que los
                        tiles de arriba: dónde cae el pasadía respecto a hoy.
                        Los tres suman la columna "Deuda actual". */}
                    {esDeudaActual && (
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#b91c1c' }} align="right">Vencido</TableCell>
                    )}
                    {esDeudaActual && (
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#a16207' }} align="right">Vence hoy</TableCell>
                    )}
                    {esDeudaActual && (
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, color: '#475569' }} align="right">Futuro</TableCell>
                    )}
                    {esDeudaActual && (
                      <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="center">Antigüedad</TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Cupo</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Utilizado</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Disponible</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12, minWidth: 130 }}>Utilización</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.agencias.map(a => {
                    const pct = a.porcentaje_utilizacion;
                    const pctColor = colorUtilizacion(pct);
                    const sinCupo = !a.cupo_credito || a.cupo_credito <= 0;
                    const antColor = colorAntiguedad(a.antiguedad_max_dias);
                    return (
                      <TableRow key={a.agencia_id} hover>
                        <TableCell>
                          <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{a.agencia_nombre}</Typography>
                          {a.ciudad && (
                            <Typography sx={{ fontSize: 11, color: '#64748b' }}>{a.ciudad}</Typography>
                          )}
                          {a.saldo_a_favor > 0 && (
                            <Typography sx={{ fontSize: 11, color: '#15803d', fontWeight: 700 }}>
                              💚 {fmtMoney(a.saldo_a_favor)} a favor
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={a.num_reservas} size="small" color="primary" />
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700,
                          color: esDeudaActual ? '#dc2626' : '#0891b2' }}>
                          {fmtMoney(a.total_cxc_periodo)}
                        </TableCell>
                        {esDeudaActual && (
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap',
                            fontWeight: a.cxc_vencido > 0 ? 700 : 400,
                            color: a.cxc_vencido > 0 ? '#b91c1c' : '#cbd5e1' }}>
                            {a.cxc_vencido > 0 ? fmtMoney(a.cxc_vencido) : '—'}
                          </TableCell>
                        )}
                        {esDeudaActual && (
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap',
                            fontWeight: a.cxc_vence_hoy > 0 ? 700 : 400,
                            color: a.cxc_vence_hoy > 0 ? '#a16207' : '#cbd5e1' }}>
                            {a.cxc_vence_hoy > 0 ? fmtMoney(a.cxc_vence_hoy) : '—'}
                          </TableCell>
                        )}
                        {esDeudaActual && (
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap',
                            color: a.cxc_futuro > 0 ? '#475569' : '#cbd5e1' }}>
                            {a.cxc_futuro > 0 ? fmtMoney(a.cxc_futuro) : '—'}
                          </TableCell>
                        )}
                        {esDeudaActual && (
                          <TableCell align="center">
                            <Chip size="small" label={`${a.antiguedad_max_dias || 0}d`}
                              sx={{ bgcolor: `${antColor}20`, color: antColor,
                                    fontWeight: 700, fontSize: 11 }} />
                          </TableCell>
                        )}
                        <TableCell align="right" sx={{ fontSize: 12, color: sinCupo ? '#94a3b8' : '#475569' }}>
                          {sinCupo ? '—' : fmtMoney(a.cupo_credito)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 12, color: '#dc2626' }}>
                          {fmtMoney(a.cupo_utilizado)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600, color: sinCupo ? '#94a3b8' : '#10b981' }}>
                          {sinCupo ? '—' : fmtMoney(a.cupo_disponible)}
                        </TableCell>
                        <TableCell sx={{ minWidth: 130 }}>
                          {sinCupo ? (
                            <Tooltip title="Esta agencia no tiene cupo de crédito configurado">
                              <Chip size="small" label="Sin cupo" sx={{ fontSize: 10, height: 20, bgcolor: '#f1f5f9', color: '#64748b' }} />
                            </Tooltip>
                          ) : (
                            <Box>
                              <LinearProgress variant="determinate"
                                value={Math.min(pct || 0, 100)}
                                sx={{
                                  height: 8, borderRadius: 4,
                                  bgcolor: '#f1f5f9',
                                  '& .MuiLinearProgress-bar': { bgcolor: pctColor, borderRadius: 4 },
                                }} />
                              <Typography sx={{ fontSize: 10, color: pctColor, mt: 0.25, fontWeight: 600 }}>
                                {pct != null ? `${pct}%` : '—'}
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Button size="small" variant="outlined"
                              onClick={() => setDetalleAgencia({
                                ...a,
                                // En "Deuda actual" no pasamos fechas → el diálogo trae TODAS
                                // las CxC vivas de la agencia (coincide con el card principal).
                                // En "CxC del período" sí las pasamos.
                                fechaDesde: esDeudaActual ? null : fechaDesde,
                                fechaHasta: esDeudaActual ? null : fechaHasta,
                                modo: esDeudaActual ? 'deuda_actual' : 'periodo',
                              })}
                              sx={{ textTransform: 'none', minWidth: 0, px: 1 }}>
                              Ver
                            </Button>
                            {esDeudaActual && a.telefono_general && (
                              <Tooltip title="Enviar recordatorio por WhatsApp">
                                <IconButton size="small"
                                  onClick={() => {
                                    const num = (a.telefono_general || '').replace(/[^0-9]/g, '');
                                    const msg = buildRecordatorioCxCMsg(a);
                                    window.open(
                                      `https://wa.me/${num}?text=${encodeURIComponent(msg)}`,
                                      '_blank'
                                    );
                                  }}
                                  sx={{ color: '#25D366', '&:hover': { bgcolor: '#25D36615' } }}>
                                  <WhatsAppIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {detalleAgencia && (
        <DetalleCxCDialog
          agencia={detalleAgencia}
          onClose={() => setDetalleAgencia(null)}
        />
      )}
    </Box>
  );
}


function KpiCard({ title, value, icon, color }) {
  return (
    <Card sx={{ ...cardStyle, p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: `${color}20`, color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#134e4a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {value}
        </Typography>
      </Box>
    </Card>
  );
}


/**
 * DetalleCxCDialog — lista de reservas CxC de una agencia, con info de cupo
 * en el encabezado (cupo total, utilizado histórico, disponible).
 */
function DetalleCxCDialog({ agencia, onClose }) {
  const empresaId = getEmpresaId();
  const [data, setData] = useState(null);
  const [pagos, setPagos] = useState([]);         // historial de pagos aplicados a la agencia
  const [loading, setLoading] = useState(true);
  const [aplicarPagoOpen, setAplicarPagoOpen] = useState(false);
  const [verRevertidos, setVerRevertidos] = useState(false);
  // Tab 0 = "Por cobrar" (default) · Tab 1 = "Historial pagos"
  const [tab, setTab] = useState(0);
  // Reserva a abrir en el SlideOver de detalle completo
  const [reservaDetalleId, setReservaDetalleId] = useState(null);
  // Set de IDs de pagos expandidos en el tab "Historial" (para ver sus distribuciones)
  const [pagosExpandidos, setPagosExpandidos] = useState(new Set());
  const togglePagoExpand = (id) => setPagosExpandidos(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (agencia.fechaDesde) params.append('fecha_desde', agencia.fechaDesde);
      if (agencia.fechaHasta) params.append('fecha_hasta', agencia.fechaHasta);
      // Cargar CxC + historial de pagos en paralelo
      const pagosParams = new URLSearchParams({
        empresa_id: empresaId,
        agencia_id: agencia.agencia_id,
        incluir_revertidos: String(verRevertidos),
        limit: '200',
      });
      const [rvs, pgs] = await Promise.all([
        fetch(`${API_BASE}/turismo/cxc/agencia/${agencia.agencia_id}/reservas?${params}`, { headers: getAuthHeaders() }).then(r => r.json()),
        fetch(`${API_BASE}/turismo/finanzas/pagos-recibidos?${pagosParams}`, { headers: getAuthHeaders() }).then(r => r.json()),
      ]);
      setData(rvs);
      setPagos(Array.isArray(pgs) ? pgs : (pgs?.pagos || []));
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [agencia.agencia_id, agencia.fechaDesde, agencia.fechaHasta, empresaId, verRevertidos]);

  useEffect(() => { cargar(); }, [cargar]);

  const info = data?.agencia;
  const cupo = Number(info?.cupo_credito || 0);
  const usado = Number(info?.cupo_utilizado || 0);
  const disp = Number(info?.cupo_disponible || 0);
  const pct = cupo > 0 ? Math.min(Math.round(usado / cupo * 100), 100) : null;
  const pctColor = colorUtilizacion(pct);
  const sinCupo = cupo <= 0;

  return (
    <StyledDialog open onClose={onClose}
      title={agencia.agencia_nombre}
      subtitle={agencia.modo === 'deuda_actual'
        ? 'Cuentas por Cobrar · toda la deuda viva'
        : `Cuentas por Cobrar · ${fmtFecha(agencia.fechaDesde)} → ${fmtFecha(agencia.fechaHasta)}`}
      icon={<AccountBalanceWalletIcon />} variant="compact" maxWidth="lg">
      <StyledDialog.Body compact>
        {loading ? <CircularProgress /> : (
          <>
            {/* Header con cupo */}
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', mb: 1 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: sinCupo ? 0 : 1 }}>
                <Chip label={`${data?.total_reservas || 0} reservas en el período`} size="small" color="primary" />
                <Chip label={`CxC del período: ${fmtMoney(data?.total_cxc_periodo)}`} size="small"
                  sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 600 }} />
                {sinCupo ? (
                  <Chip label="Sin cupo de crédito configurado" size="small"
                    sx={{ bgcolor: '#f1f5f9', color: '#64748b' }} />
                ) : (
                  <>
                    <Chip label={`Cupo: ${fmtMoney(cupo)}`} size="small"
                      sx={{ bgcolor: '#f0fdf4', color: '#15803d', fontWeight: 600 }} />
                    <Chip label={`Utilizado hist.: ${fmtMoney(usado)}`} size="small"
                      sx={{ bgcolor: '#fef2f2', color: '#dc2626', fontWeight: 600 }} />
                    <Chip label={`Disponible: ${fmtMoney(disp)}`} size="small"
                      sx={{ bgcolor: disp > 0 ? '#f0fdf4' : '#fef2f2', color: disp > 0 ? '#15803d' : '#dc2626', fontWeight: 700 }} />
                  </>
                )}
              </Box>
              {!sinCupo && (
                <Box>
                  <LinearProgress variant="determinate" value={pct}
                    sx={{ height: 10, borderRadius: 5, bgcolor: '#e2e8f0',
                          '& .MuiLinearProgress-bar': { bgcolor: pctColor, borderRadius: 5 } }} />
                  <Typography sx={{ fontSize: 11, color: pctColor, mt: 0.5, fontWeight: 600 }}>
                    {pct}% del cupo utilizado
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Tabs: Por cobrar / Historial pagos */}
            <Tabs value={tab} onChange={(_, v) => setTab(v)}
              sx={{ borderBottom: '1px solid #e2e8f0', mb: 1.5, minHeight: 40,
                    '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: 12, minHeight: 40, py: 0.5 },
                    '& .Mui-selected': { color: '#0891b2' },
                    '& .MuiTabs-indicator': { bgcolor: '#0891b2', height: 3, borderRadius: 2 } }}>
              <Tab label={`🔴 Por cobrar (${data?.reservas?.length || 0})`} />
              <Tab label={`💰 Historial pagos (${pagos.length})`} />
            </Tabs>

            {/* TAB 0: POR COBRAR */}
            {tab === 0 && (
              <>
                <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Alert severity="info" sx={{ fontSize: 12, flex: 1, minWidth: 260 }}>
                    <strong>Saldo pendiente</strong> = lo que la agencia realmente adeuda hoy (ya considera abonos y cobros en muelle).
                  </Alert>
                  {!esSoloConsulta() && (
                  <Button variant="contained" startIcon={<PaymentIcon />}
                    onClick={() => setAplicarPagoOpen(true)}
                    sx={{
                      textTransform: 'none', fontWeight: 700,
                      background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
                      boxShadow: '0 8px 20px rgba(8, 145, 178, 0.35)',
                    }}>
                    Aplicar pago de la agencia
                  </Button>
                  )}
                </Box>

                {(!data?.reservas || data.reservas.length === 0) ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography sx={{ color: '#64748b', fontSize: 13 }}>
                      Sin CxC en este período.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer component={Paper} variant="outlined"
                    sx={{
                      // `flexShrink: 0` es lo que arregla el aplastamiento: el
                      // cuerpo del diálogo es una columna flex y, sin esto, la
                      // tabla cedía su alto a los bloques de arriba —cupo,
                      // avisos, botón— y quedaba de dos renglones. El alto
                      // mínimo garantiza que siempre se vean varias filas.
                      maxHeight: '72vh', minHeight: 440, flexShrink: 0,
                      overflowX: 'auto',
                    }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 700, fontSize: 11 } }}>
                          <TableCell>Código</TableCell>
                          <TableCell>Cliente</TableCell>
                          <TableCell>Paquete</TableCell>
                          <TableCell>Creada por</TableCell>
                          <TableCell>Creada</TableCell>
                          <TableCell align="center">Antigüedad</TableCell>
                          <TableCell>Salida</TableCell>
                          <TableCell align="right">Precio total</TableCell>
                          <TableCell align="right">Comisión</TableCell>
                          <TableCell align="right">Saldo pendiente</TableCell>
                          <TableCell>Factura</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(data?.reservas || []).map(r => {
                          const antColor = colorAntiguedad(r.dias_antiguedad);
                          const saldoReal = Number(r.saldo_pendiente ?? r.cxc_neto ?? 0);
                          // Semáforo de fondo por antigüedad:
                          //   0d    → verde pálido (aún no arranca el reloj)
                          //   1-5d  → amarillo pálido (deuda joven)
                          //   6+d   → rojo pálido (deuda vencida)
                          const dias = r.dias_antiguedad ?? 0;
                          // Las saldadas solo aparecen en la vista del período,
                          // que cuenta la historia. Van en gris para que no se
                          // confundan con deuda viva.
                          const saldada = r.saldada || saldoReal <= 0.01;
                          const bgRow =
                            saldada       ? '#f8fafc' :   // gris: ya pagada
                            dias === 0    ? '#ecfdf5' :   // verde muy pálido
                            dias <= 5     ? '#fefce8' :   // amarillo muy pálido
                                            '#fef2f2';    // rojo muy pálido
                          return (
                            <TableRow key={r.id} hover
                              onClick={() => setReservaDetalleId(r.id)}
                              sx={{ bgcolor: bgRow, cursor: 'pointer',
                                    '&:hover': { bgcolor: `${bgRow} !important`, filter: 'brightness(0.95)' } }}>
                              <TableCell sx={{ fontSize: 11 }}>{r.codigo_reserva}</TableCell>
                              <TableCell sx={{ fontSize: 11 }}>{r.nombre_cliente}</TableCell>
                              <TableCell sx={{ fontSize: 11 }}>{r.paquete_nombre || '—'}</TableCell>
                              <TableCell sx={{ fontSize: 11 }}>
                                <Typography sx={{ fontSize: 11, color: '#0f172a' }}>
                                  {r.creado_por_nombre || '—'}
                                </Typography>
                                {r.creado_por_origen && (
                                  <Typography sx={{ fontSize: 9, color: '#64748b' }}>
                                    {r.creado_por_origen}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ fontSize: 11 }}>{fmtFecha(r.fecha_reserva)}</TableCell>
                              <TableCell align="center">
                                {/* En una reserva saldada el reloj se detuvo
                                    el día del pago. Sin decir hasta cuándo,
                                    el número parece un error. */}
                                <Chip size="small" label={`${r.dias_antiguedad ?? 0}d`}
                                  sx={{ bgcolor: saldada ? '#e2e8f0' : `${antColor}20`,
                                        color: saldada ? '#475569' : antColor,
                                        fontWeight: 700, fontSize: 10, height: 20 }} />
                                {saldada && r.fecha_saldado && (
                                  <Typography sx={{ fontSize: 9, color: '#64748b' }}>
                                    hasta {fmtFecha(r.fecha_saldado)}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ fontSize: 11 }}>{fmtFecha(r.fecha_salida)}</TableCell>
                              <TableCell align="right" sx={{ fontSize: 11 }}>{fmtMoney(r.precio_total)}</TableCell>
                              <TableCell align="right" sx={{ fontSize: 11, color: '#a16207' }}>
                                {fmtMoney(r.comision_valor)}
                                {r.descuento_comision > 0 && (
                                  <Typography component="span" sx={{ fontSize: 9, color: '#7c3aed', display: 'block' }}>
                                    (desc {fmtMoney(r.descuento_comision)})
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700,
                                          color: saldada ? '#166534' : '#dc2626' }}>
                                {saldada ? (
                                  <Chip size="small" label="Pagada"
                                    sx={{ bgcolor: '#dcfce7', color: '#166534',
                                          fontWeight: 700, fontSize: 10, height: 20 }} />
                                ) : fmtMoney(saldoReal)}
                                {saldada && r.pagado > 0 && (
                                  <Typography sx={{ fontSize: 9, color: '#64748b' }}>
                                    {fmtMoney(r.pagado)}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ fontSize: 10 }}>
                                {r.numero_factura ? (
                                  <>
                                    <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#134e4a' }}>{r.numero_factura}</Typography>
                                    <Typography sx={{ fontSize: 9, color: '#64748b' }}>{fmtFecha(r.fecha_factura)}</Typography>
                                  </>
                                ) : (
                                  <Typography sx={{ fontSize: 10, color: '#94a3b8', fontStyle: 'italic' }}>—</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                      {/* Footer con total al pie */}
                      <TableHead>
                        <TableRow sx={{ '& th': { bgcolor: '#fef2f2', borderTop: '2px solid #dc2626' } }}>
                          <TableCell colSpan={9} sx={{ fontSize: 12, fontWeight: 800, color: '#7f1d1d', textAlign: 'right' }}>
                            TOTAL SALDO PENDIENTE:
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: 13, fontWeight: 900, color: '#dc2626' }}>
                            {fmtMoney(data?.total_saldo_pendiente ?? 0)}
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}

            {/* TAB 1: HISTORIAL DE PAGOS */}
            {tab === 1 && (
              <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0d9488', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Historial de pagos de {agencia.agencia_nombre}
                </Typography>
                <Button size="small"
                  onClick={() => setVerRevertidos(v => !v)}
                  sx={{ textTransform: 'none', fontSize: 11, color: '#64748b' }}>
                  {verRevertidos ? 'Ocultar revertidos' : 'Ver también revertidos'}
                </Button>
              </Box>
              {pagos.length === 0 ? (
                <Alert severity="info" sx={{ fontSize: 12 }}>
                  Todavía no se ha registrado ningún pago de esta agencia.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined"
                  sx={{ maxHeight: '55vh', minHeight: 260, flexShrink: 0, overflowX: 'auto' }}>
                  <Table size="small" stickyHeader sx={{ minWidth: 820 }}>
                    <TableHead>
                      <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 700, fontSize: 11 } }}>
                        <TableCell sx={{ minWidth: 90, whiteSpace: 'nowrap' }}>Fecha</TableCell>
                        <TableCell sx={{ minWidth: 110, whiteSpace: 'nowrap' }}>Medio</TableCell>
                        <TableCell sx={{ minWidth: 160 }}>Cuenta destino</TableCell>
                        <TableCell sx={{ minWidth: 120 }}>Referencia</TableCell>
                        <TableCell align="right" sx={{ minWidth: 100, whiteSpace: 'nowrap' }}>Transferido</TableCell>
                        <TableCell align="right" sx={{ minWidth: 90, whiteSpace: 'nowrap' }}>Retención</TableCell>
                        <TableCell align="right" sx={{ minWidth: 110, whiteSpace: 'nowrap' }}>Total aplicado</TableCell>
                        <TableCell align="center" sx={{ minWidth: 60, whiteSpace: 'nowrap' }}>Reservas</TableCell>
                        <TableCell align="center" sx={{ minWidth: 70, whiteSpace: 'nowrap' }}>Soporte</TableCell>
                        <TableCell align="center" sx={{ minWidth: 80, whiteSpace: 'nowrap' }}>Estado</TableCell>
                        <TableCell align="center" sx={{ minWidth: 50, whiteSpace: 'nowrap' }}>PDF</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagos.map(p => {
                        const revertido = !!p.revertido;
                        const totalAplicado = Number(p.monto_total_aplicado ?? ((p.monto_total || 0) + (p.monto_retencion || 0)));
                        const dists = p.distribuciones || [];
                        const nDists = p.num_distribuciones ?? dists.length;
                        const expandido = pagosExpandidos.has(p.id);
                        return (
                          <React.Fragment key={p.id}>
                          <TableRow hover sx={{
                            opacity: revertido ? 0.55 : 1,
                            // Fila padre resaltada cuando el expandible está abierto (paleta Turismo)
                            bgcolor: expandido ? '#f0fdfa' : undefined,
                          }}>
                            <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtFecha(p.fecha_pago)}</TableCell>
                            <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>{p.metodo_pago}</TableCell>
                            <TableCell sx={{ fontSize: 11 }}>{p.cuenta_destino_nombre || '—'}</TableCell>
                            <TableCell sx={{ fontSize: 11, fontFamily: 'monospace' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box component="span" sx={{ minWidth: 0 }}>{p.referencia || '—'}</Box>
                                {p.notas && (
                                  <Tooltip
                                    arrow
                                    placement="top"
                                    title={
                                      <Box sx={{ fontFamily: 'system-ui', fontSize: 12, whiteSpace: 'pre-wrap', maxWidth: 320 }}>
                                        <Box sx={{ fontWeight: 700, mb: 0.5 }}>Notas del pago</Box>
                                        {p.notas}
                                      </Box>
                                    }>
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: '#0891b2', cursor: 'help' }} />
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 11, fontWeight: 600 }}>
                              {fmtMoney(p.monto_total)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 11, color: p.monto_retencion > 0 ? '#a16207' : '#94a3b8' }}>
                              {p.monto_retencion > 0 ? fmtMoney(p.monto_retencion) : '—'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 12, fontWeight: 800, color: '#0891b2' }}>
                              {fmtMoney(totalAplicado)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: 11 }}>
                              {nDists > 0 ? (
                                <Chip size="small" clickable
                                  onClick={() => togglePagoExpand(p.id)}
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                      <span>{expandido ? '▼' : '▶'}</span>
                                      <span>{nDists}</span>
                                    </Box>
                                  }
                                  sx={{ bgcolor: expandido ? '#0d9488' : '#e0f2fe',
                                        color: expandido ? '#fff' : '#0369a1',
                                        fontWeight: 700, height: 22,
                                        '&:hover': { bgcolor: expandido ? '#0f766e' : '#bae6fd' } }} />
                              ) : (
                                <Chip size="small" label="—"
                                  sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 700, height: 20 }} />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {p.comprobante_url ? (
                                <Tooltip title="Ver evidencia (voucher/transferencia)" arrow>
                                  <IconButton size="small"
                                    href={
                                      p.comprobante_url.startsWith('http')
                                        ? p.comprobante_url
                                        : `${API_BASE}${p.comprobante_url}`
                                    }
                                    target="_blank" rel="noopener"
                                    sx={{ color: '#0891b2' }}>
                                    <ReceiptLongIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Typography sx={{ fontSize: 11, color: '#cbd5e1' }}>—</Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {revertido ? (
                                <Chip size="small" label="Revertido"
                                  sx={{ bgcolor: '#fef2f2', color: '#dc2626', fontWeight: 700, height: 20, fontSize: 10 }} />
                              ) : (
                                <Chip size="small" label="Aplicado"
                                  sx={{ bgcolor: '#f0fdf4', color: '#15803d', fontWeight: 700, height: 20, fontSize: 10 }} />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Descargar comprobante PDF de este pago" arrow>
                                <IconButton size="small"
                                  onClick={() => window.open(
                                    `${API_BASE}/turismo/finanzas/pagos-recibidos/${p.id}/pdf`,
                                    '_blank'
                                  )}
                                  sx={{ color: '#dc2626' }}>
                                  <PictureAsPdfIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                          {/* Fila expandida con detalle de distribuciones */}
                          {expandido && (
                            <TableRow>
                              <TableCell colSpan={11} sx={{ p: 0, bgcolor: '#f0fdfa', borderBottom: '2px solid #0d9488', borderLeft: '4px solid #0d9488' }}>
                                <Box sx={{ p: 1.5 }}>
                                  <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#0369a1',
                                                    textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
                                    Distribución de este pago ({dists.length} reservas)
                                  </Typography>
                                  <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow sx={{ '& th': { bgcolor: '#eff6ff', fontWeight: 700, fontSize: 10.5, py: 0.75 } }}>
                                          <TableCell>Reserva</TableCell>
                                          <TableCell>Cliente</TableCell>
                                          <TableCell align="right">Monto aplicado</TableCell>
                                          <TableCell align="right">Saldo actual</TableCell>
                                          <TableCell align="center">Estado</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {dists.map(d => {
                                          const cerrada = d.estado_aplicacion === 'cerrada';
                                          const chipColor = cerrada ? '#059669' : '#d97706';
                                          const chipBg    = cerrada ? '#ecfdf5' : '#fefce8';
                                          const chipLabel = cerrada ? 'Cerrada' : 'Parcial';
                                          return (
                                            <TableRow key={d.id} hover
                                              onClick={() => setReservaDetalleId(d.reservacion_id)}
                                              sx={{ cursor: 'pointer' }}>
                                              <TableCell sx={{ fontSize: 11, fontFamily: 'monospace', color: '#0369a1' }}>
                                                {d.codigo_reserva}
                                              </TableCell>
                                              <TableCell sx={{ fontSize: 11 }}>{d.nombre_cliente || '—'}</TableCell>
                                              <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700, color: '#0891b2' }}>
                                                {fmtMoney(d.monto_aplicado)}
                                              </TableCell>
                                              <TableCell align="right" sx={{ fontSize: 11,
                                                color: (d.saldo_actual || 0) > 0 ? '#dc2626' : '#059669' }}>
                                                {fmtMoney(d.saldo_actual)}
                                              </TableCell>
                                              <TableCell align="center">
                                                <Chip size="small" label={chipLabel}
                                                  sx={{ bgcolor: chipBg, color: chipColor,
                                                        fontWeight: 700, fontSize: 10, height: 20 }} />
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                  <Typography sx={{ fontSize: 9.5, color: '#94a3b8', fontStyle: 'italic', mt: 0.5 }}>
                                    Click en una fila para ver el detalle financiero de la reserva.
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                    {/* Footer con total pagado (suma de pagos NO revertidos) */}
                    <TableHead>
                      <TableRow sx={{ '& th': { bgcolor: '#f0fdf4', borderTop: '2px solid #059669' } }}>
                        <TableCell colSpan={6} sx={{ fontSize: 12, fontWeight: 800, color: '#134e4a', textAlign: 'right' }}>
                          TOTAL PAGADO:
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 13, fontWeight: 900, color: '#059669' }}>
                          {fmtMoney(
                            (pagos || [])
                              .filter(p => !p.revertido)
                              .reduce((s, p) => s + Number(
                                p.monto_total_aplicado ?? ((p.monto_total || 0) + (p.monto_retencion || 0))
                              ), 0)
                          )}
                        </TableCell>
                        <TableCell colSpan={4} />
                      </TableRow>
                    </TableHead>
                  </Table>
                </TableContainer>
              )}
              </Box>
            )}
          </>
        )}
      </StyledDialog.Body>
      <StyledDialog.CompactActions>
        <StyledDialog.PrimaryButton fullWidth onClick={onClose}>Cerrar</StyledDialog.PrimaryButton>
      </StyledDialog.CompactActions>
      {aplicarPagoOpen && (
        <AplicarPagoDialog
          agencia={agencia}
          cupo={{ cupo, usado, disp }}
          onClose={(reloaded) => { setAplicarPagoOpen(false); if (reloaded) cargar(); }}
        />
      )}
      {/* Modal financiero enfocado (solo lectura + auditoría). */}
      {reservaDetalleId && (
        <ReservaFinancieroModal
          reservaId={reservaDetalleId}
          onClose={() => setReservaDetalleId(null)}
        />
      )}
    </StyledDialog>
  );
}




/**
 * AplicarPagoDialog — registrar un pago que hizo la agencia y distribuirlo a
 * sus reservas CxC pendientes. Usa /turismo/finanzas/pagos-recibidos que ya
 * existe con toda la mecánica de validación y reversal.
 */
function AplicarPagoDialog({ agencia, cupo, onClose }) {
  const empresaId = getEmpresaId();
  // Cabecera del pago
  const [fechaPago, setFechaPago] = useState(today());
  const [metodoPago, setMetodoPago] = useState('transferencia');
  const [cuentaDestinoId, setCuentaDestinoId] = useState('');
  const [referencia, setReferencia] = useState('');
  const [notas, setNotas] = useState('');
  // Retención aplicada por la agencia (opcional). No llega como cash pero reduce
  // el saldo pendiente igual que la transferencia. Total aplicado al saldo =
  // monto transferido + retención.
  const [montoRetencion, setMontoRetencion] = useState('');
  // Distribución manual
  const [reservas, setReservas] = useState([]);   // reservas CxC pendientes
  const [distribucion, setDistribucion] = useState({});   // {reservacion_id: monto}
  // Config
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  // Cruce contra comisión pendiente (asiento contable, no pago externo).
  // Cuando `metodoPago === 'cruce'`, se ocultan cuenta destino/comprobante/
  // retención y aparece una tabla de comisiones pendientes con checkboxes.
  // El monto a distribuir en las CxC = suma de comisiones seleccionadas.
  // Al guardar se llama a /turismo/cruce/aplicar-manual (no /pagos-recibidos).
  const [comisionesPendientes, setComisionesPendientes] = useState([]);   // [{id, codigo, cliente, comision}, ...]
  const [comisionesSel, setComisionesSel] = useState({});                 // {reservacion_id: bool}

  // Comprobante (evidencia) — se sube antes de guardar el pago. Vision extrae
  // monto y cuenta destino para pre-llenar los campos. La imagen queda
  // vinculada como VoucherPago por cada reserva de la distribución.
  // El hook reutilizable maneja upload + análisis + estados.
  const comprobante = useComprobanteUploader({
    apiBase: API_BASE,
    getAuthHeaders,
    empresaId,
    getMontoEsperado: () => totalPendienteAgenciaRef.current,
  });
  // Ref para exponer `totalPendienteAgencia` sin recrear el hook — se pobla
  // más abajo con useEffect (evita ciclos en el getMontoEsperado closure).
  const totalPendienteAgenciaRef = React.useRef(0);

  // Carga inicial: reservas con saldo + cuentas destino + comisiones pendientes
  // (para el modo "cruce"). Las comisiones se cargan desde el detalle de cruce
  // por agencia, que ya trae la lista con la fórmula correcta (incluye tipo
  // anticipo como pendiente).
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [rvsRes, cuentasRes, cruceRes] = await Promise.all([
          fetch(`${API_BASE}/turismo/finanzas/agencias/${agencia.agencia_id}/reservas-con-saldo`, { headers: getAuthHeaders() }),
          fetch(`${API_BASE}/turismo/cuentas-destino-pago?empresa_id=${empresaId}&solo_activos=true`, { headers: getAuthHeaders() }),
          fetch(`${API_BASE}/turismo/cruce/agencia/${agencia.agencia_id}`, { headers: getAuthHeaders() }),
        ]);
        const rvs = await rvsRes.json();
        const cts = await cuentasRes.json();
        setReservas(Array.isArray(rvs) ? rvs : []);
        setCuentas(Array.isArray(cts) ? cts : (cts?.cuentas || []));
        if (cruceRes.ok) {
          const cj = await cruceRes.json();
          setComisionesPendientes(cj.comisiones_pendientes || []);
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [agencia.agencia_id, empresaId]);

  const totalPendienteAgencia = useMemo(
    () => reservas.reduce((s, r) => s + Number(r.saldo_pendiente || 0), 0),
    [reservas]
  );
  const totalDistribuido = useMemo(
    () => Object.values(distribucion).reduce((s, v) => s + (Number(v) || 0), 0),
    [distribucion]
  );
  const retencionNum = Math.max(Number(montoRetencion) || 0, 0);
  // El total distribuido debe cuadrar con transferido + retención. La UI
  // deduce cuánto es el efectivo transferido a la cuenta (total − retención).
  const montoTransferido = Math.max(totalDistribuido - retencionNum, 0);

  const esCruce = metodoPago === 'cruce';
  const esRetencion = metodoPago === 'retencion';
  // Ambos modos son "asiento contable" — no llevan cuenta destino ni comprobante
  const esAsientoContable = esCruce || esRetencion;
  // Tipo de retención (solo aplica cuando esRetencion)
  const [tipoRetencion, setTipoRetencion] = useState('ica');

  // Comisiones seleccionadas para cruzar (solo aplica en modo cruce)
  const totalComisionSeleccionada = useMemo(() => {
    if (!esCruce) return 0;
    return comisionesPendientes.reduce((s, c) => {
      return s + (comisionesSel[c.id] ? Number(c.comision || 0) : 0);
    }, 0);
  }, [esCruce, comisionesPendientes, comisionesSel]);

  // Validaciones — bifurcadas por modo
  const errores = useMemo(() => {
    const e = [];
    if (!fechaPago) e.push('Falta la fecha del pago.');
    if (!metodoPago) e.push('Selecciona el medio de pago.');
    if (esCruce) {
      // Cruce: NO exige cuenta destino, comprobante ni retención
      if (totalComisionSeleccionada <= 0) {
        e.push('Selecciona al menos una comisión pendiente para cruzar.');
      }
      if (totalDistribuido <= 0) {
        e.push('Debes distribuir el cruce en al menos una reserva CxC.');
      }
      if (totalDistribuido > totalComisionSeleccionada + 0.01) {
        e.push(`La distribución (${fmtMoney(totalDistribuido)}) excede la comisión seleccionada (${fmtMoney(totalComisionSeleccionada)}).`);
      }
      for (const r of reservas) {
        const asignado = Number(distribucion[r.id] || 0);
        if (asignado > Number(r.saldo_pendiente || 0) + 0.01) {
          e.push(`${r.codigo_reserva}: monto asignado (${fmtMoney(asignado)}) excede su saldo pendiente (${fmtMoney(r.saldo_pendiente)}).`);
        }
      }
    } else if (esRetencion) {
      // Retención: NO exige cuenta destino, comprobante ni retención adicional
      if (!tipoRetencion) e.push('Selecciona el tipo de retención (ICA, retefuente, IVA, otro).');
      if (totalDistribuido <= 0) e.push('Debes indicar el monto de retención en al menos una reserva.');
      for (const r of reservas) {
        const asignado = Number(distribucion[r.id] || 0);
        if (asignado > Number(r.saldo_pendiente || 0) + 0.01) {
          e.push(`${r.codigo_reserva}: retención asignada (${fmtMoney(asignado)}) excede su saldo pendiente (${fmtMoney(r.saldo_pendiente)}).`);
        }
      }
    } else {
      // Pago externo (transferencia, efectivo, nequi, etc.)
      if (!cuentaDestinoId) e.push('Selecciona la cuenta destino.');
      // El comprobante es obligatorio salvo en efectivo, que se recibe en mano
      // y no siempre deja tirilla. Sin evidencia el pago queda con la
      // referencia que alguien tecleó, que parece respaldo y no lo es: el
      // estado de cuenta muestra «—» en Soporte y la reserva queda marcada
      // como «voucher pendiente de subsanar» sin que nadie lo vea.
      if (metodoPago !== 'efectivo' && !comprobante.url) {
        e.push('Adjunta el comprobante del pago. Sin él, el pago queda sin respaldo auditable.');
      }
      if (totalDistribuido <= 0) e.push('Debes asignar el pago a al menos una reserva.');
      if (retencionNum > totalDistribuido) e.push('La retención no puede ser mayor al total distribuido.');
      for (const r of reservas) {
        const asignado = Number(distribucion[r.id] || 0);
        if (asignado > Number(r.saldo_pendiente || 0) + 0.01) {
          e.push(`${r.codigo_reserva}: monto asignado (${fmtMoney(asignado)}) excede su saldo pendiente (${fmtMoney(r.saldo_pendiente)}).`);
        }
      }
    }
    return e;
  }, [fechaPago, metodoPago, cuentaDestinoId, totalDistribuido, retencionNum, distribucion, reservas,
      esCruce, esRetencion, tipoRetencion, totalComisionSeleccionada, comprobante.url]);

  const puedeGuardar = !saving && errores.length === 0;

  const asignarSaldoTotal = (rid, saldo) => {
    setDistribucion(d => ({ ...d, [rid]: saldo }));
  };
  const cambiarMonto = (rid, valor) => {
    setDistribucion(d => ({ ...d, [rid]: valor.replace(/[^\d.]/g, '') }));
  };

  // Mantener actualizado el ref de "monto esperado" para el hook
  React.useEffect(() => {
    totalPendienteAgenciaRef.current = totalPendienteAgencia;
  }, [totalPendienteAgencia]);

  // Wrapper del handleFile del hook: además, pre-pobla campos vacíos con lo
  // que Vision detectó (referencia + cuenta destino sugerida).
  const handleFileUpload = async (file) => {
    const result = await comprobante.handleFile(file);
    const analJson = result?.analisis;
    if (analJson) {
      if (analJson.referencia && !referencia) {
        setReferencia(String(analJson.referencia));
      }
      const cid = analJson?.cuenta_destino_match?.cuenta_id;
      if (cid && !cuentaDestinoId) {
        setCuentaDestinoId(cid);
      }
    }
  };

  // Discrepancia entre monto detectado por Vision y el total distribuido.
  // Solo alerta — no bloquea. `distribuido` incluye retención.
  const discrepanciaComp = useMemo(() => {
    const detectado = Number(comprobante.analisis?.monto || 0);
    if (!detectado || totalDistribuido <= 0) return null;
    const diff = Math.abs(detectado - totalDistribuido);
    const pct = (diff / detectado) * 100;
    if (pct <= 2) return null; // tolerancia de 2%
    return { detectado, distribuido: totalDistribuido, diff, pct };
  }, [comprobante.analisis, totalDistribuido]);

  const handleGuardar = async () => {
    setErr('');
    setSaving(true);
    try {
      let res;
      if (esCruce) {
        // Cruce contable — llama al endpoint especializado
        const distribuciones_cxc = Object.entries(distribucion)
          .map(([rid, m]) => ({ reservacion_id: Number(rid), monto: Number(m) || 0 }))
          .filter(x => x.monto > 0);
        const comisiones_reservacion_ids = Object.entries(comisionesSel)
          .filter(([, sel]) => sel)
          .map(([id]) => Number(id));
        const body = {
          empresa_id: empresaId,
          agencia_id: agencia.agencia_id,
          comisiones_reservacion_ids,
          distribuciones_cxc,
          notas: (notas.trim() || `Cruce aplicado desde modal AplicarPago de ${agencia.agencia_nombre}`),
        };
        res = await fetch(`${API_BASE}/turismo/cruce/aplicar-manual`, {
          method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body),
        });
      } else if (esRetencion) {
        // Retención — asiento contable puro, sin cuenta destino ni comprobante
        const distribuciones = Object.entries(distribucion)
          .map(([rid, m]) => ({ reservacion_id: Number(rid), monto: Number(m) || 0 }))
          .filter(x => x.monto > 0);
        const body = {
          empresa_id: empresaId,
          agencia_id: agencia.agencia_id,
          tipo_retencion: tipoRetencion,
          distribuciones,
          referencia: referencia.trim() || null,
          notas: notas.trim() || null,
        };
        res = await fetch(`${API_BASE}/turismo/retenciones/aplicar`, {
          method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body),
        });
      } else {
        // Pago externo — flujo original
        const distribuciones = Object.entries(distribucion)
          .map(([rid, m]) => ({ reservacion_id: Number(rid), monto_aplicado: Number(m) || 0 }))
          .filter(x => x.monto_aplicado > 0);
        const body = {
          empresa_id: empresaId,
          agencia_id: agencia.agencia_id,
          fecha_pago: fechaPago,
          // monto_total = efectivo/transferido a la cuenta destino (excluye retención)
          monto_total: montoTransferido,
          monto_retencion: retencionNum,
          metodo_pago: metodoPago,
          cuenta_destino_id: Number(cuentaDestinoId),
          referencia: referencia.trim() || null,
          notas: notas.trim() || null,
          comprobante_url: comprobante.url || null,
          distribuciones,
        };
        res = await fetch(`${API_BASE}/turismo/finanzas/pagos-recibidos`, {
          method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body),
        });
      }
      const j = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Pydantic devuelve `detail` como array de objetos {loc, msg, type};
        // si lo pasamos directo a setErr, React explota y deja pantalla blanca.
        const d = j.detail;
        const msg = Array.isArray(d)
          ? d.map(x => x?.msg || JSON.stringify(x)).join(' · ')
          // El control de comprobante repetido responde un objeto con
          // `message`; sin este caso el operador veía "Error al registrar el
          // pago" y no se enteraba de que la imagen ya se había usado.
          : (d && typeof d === 'object' && d.message) ? d.message
          : (typeof d === 'string' ? d : 'Error al registrar el pago');
        setErr(msg);
        setSaving(false);
        return;
      }
      onClose(true);
    } catch (e) {
      setErr('Error de red');
    }
    setSaving(false);
  };

  return (
    <StyledDialog open onClose={() => !saving && onClose(false)}
      title={`Aplicar pago de ${agencia.agencia_nombre}`}
      subtitle="Registra el pago recibido y distribúyelo entre las reservas CxC pendientes"
      icon={<PaymentIcon />} variant="compact" maxWidth="lg">
      <StyledDialog.Body compact>
        {loading ? <CircularProgress /> : (
          <>
            {/* Estado de la agencia */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Chip size="small" label={`${reservas.length} reservas con saldo pendiente`}
                sx={{ bgcolor: '#f0f9ff', color: '#0369a1', fontWeight: 600 }} />
              <Chip size="small" label={`Total pendiente: ${fmtMoney(totalPendienteAgencia)}`}
                sx={{ bgcolor: '#fef2f2', color: '#dc2626', fontWeight: 700 }} />
              {cupo.cupo > 0 && (
                <Chip size="small" label={`Cupo disp.: ${fmtMoney(cupo.disp)}`}
                  sx={{ bgcolor: '#f0fdf4', color: '#15803d', fontWeight: 600 }} />
              )}
            </Box>

            {/* === Bloque de comprobante (solo si NO es cruce) === */}
            {!esCruce && (
            <ComprobanteBlock
              url={comprobante.url}
              nombre={comprobante.nombre}
              subiendo={comprobante.subiendo}
              analizando={comprobante.analizando}
              analisis={comprobante.analisis}
              error={comprobante.error}
              cuentaDestinoId={cuentaDestinoId}
              onFile={handleFileUpload}
              onQuitar={comprobante.quitar}
              discrepancia={discrepanciaComp}
              fmtMoney={fmtMoney}
            />
            )}

            {/* Cabecera del pago — grid organizado por filas explícitas para evitar
                que el helperText de un TextField empuje la tabla de distribución
                fuera del viewport. Cada fila tiene alturas homogéneas. */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5, mb: 1 }}>
              {/* Fila 1: fecha · medio · cuenta destino (o placeholder si cruce) */}
              <TextField sx={inputStyles} type="date" label="Fecha del pago"
                InputLabelProps={{ shrink: true }}
                value={fechaPago} onChange={e => setFechaPago(e.target.value)} />
              <FormControl sx={inputStyles} size="small">
                <InputLabel>Medio de pago</InputLabel>
                <Select value={metodoPago} label="Medio de pago"
                  onChange={e => setMetodoPago(e.target.value)}>
                  <MenuItem value="transferencia">Transferencia</MenuItem>
                  <MenuItem value="efectivo">Efectivo</MenuItem>
                  <MenuItem value="nequi">Nequi</MenuItem>
                  <MenuItem value="daviplata">Daviplata</MenuItem>
                  <MenuItem value="cheque">Cheque</MenuItem>
                  <MenuItem value="tarjeta">Tarjeta</MenuItem>
                  <MenuItem value="otro">Otro</MenuItem>
                  <MenuItem value="cruce" disabled={comisionesPendientes.length === 0}>
                    🔀 Cruce con comisión pendiente
                    {comisionesPendientes.length === 0 ? ' (sin comisiones pendientes)' : ''}
                  </MenuItem>
                  <MenuItem value="retencion">
                    📋 Retención aplicada por la agencia
                  </MenuItem>
                </Select>
              </FormControl>
              {esRetencion ? (
                <FormControl sx={inputStyles} size="small">
                  <InputLabel>Tipo de retención *</InputLabel>
                  <Select value={tipoRetencion} label="Tipo de retención *"
                    onChange={e => setTipoRetencion(e.target.value)}>
                    <MenuItem value="ica">ICA (municipal)</MenuItem>
                    <MenuItem value="retefuente">Retefuente</MenuItem>
                    <MenuItem value="iva">IVA</MenuItem>
                    <MenuItem value="otro">Otro</MenuItem>
                  </Select>
                </FormControl>
              ) : esCruce ? (
                <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, borderRadius: 1, bgcolor: '#e0f2fe', border: '1px dashed #7dd3fc' }}>
                  <Typography sx={{ fontSize: 11.5, color: '#075985', fontWeight: 600 }}>
                    Cruce contable — sin cuenta destino
                  </Typography>
                </Box>
              ) : (
                <FormControl sx={inputStyles} size="small" error={!cuentaDestinoId}>
                  <InputLabel>Cuenta destino *</InputLabel>
                  <Select value={cuentaDestinoId} label="Cuenta destino *"
                    onChange={e => setCuentaDestinoId(e.target.value)}>
                    <MenuItem value="" disabled><em>Selecciona una cuenta</em></MenuItem>
                    {cuentas.map(c => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.nombre}{c.tipo ? ` (${c.tipo})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {/* Fila 2: referencia (2 cols) · retención (1 col) */}
              {/* Cruce: sin referencia ni campo retención (asiento contable puro) */}
              {/* Retención: SÍ referencia (nº certificado), sin campo retención mixta */}
              {/* Pago externo: referencia + retención */}
              {!esCruce && (
              <TextField sx={{ ...inputStyles,
                               gridColumn: { xs: '1', sm: esRetencion ? '1 / 4' : '1 / 3' } }}
                label={esRetencion ? 'Referencia / nº certificado' : 'Referencia (número de transacción)'}
                value={referencia} onChange={e => setReferencia(e.target.value)} />
              )}
              {!esAsientoContable && (
              <TextField sx={inputStyles} type="number"
                label="Retención aplicada"
                placeholder="0"
                value={montoRetencion}
                onChange={e => setMontoRetencion(e.target.value.replace(/[^\d.]/g, ''))}
                inputProps={{ min: 0, step: 100 }} />
              )}
              {/* Fila 3: notas (span 3) */}
              <TextField sx={{ ...inputStyles, gridColumn: { xs: '1', sm: '1 / 4' } }}
                label="Notas (opcional)"
                value={notas} onChange={e => setNotas(e.target.value)} />
            </Box>
            {!esAsientoContable && (
              <Typography sx={{ fontSize: 10.5, color: '#64748b', mt: -0.5, mb: 1, fontStyle: 'italic' }}>
                La retención (retefuente/ICA/IVA) reduce la CxC pero no llega como cash a la cuenta destino.
              </Typography>
            )}
            {esRetencion && (
              <Alert severity="info" icon={false} sx={{ fontSize: 11.5, py: 0.5, mb: 1 }}>
                📋 <strong>Retención {tipoRetencion.toUpperCase()}</strong> — asiento contable puro. Escribe en la tabla de abajo cuánto retuvo la agencia por cada reserva (típicamente $5.000, $10.000, etc.). El saldo pendiente bajará por ese monto.
              </Alert>
            )}

            {/* === MODO CRUCE: selector de comisiones pendientes === */}
            {esCruce && (
              <Box sx={{ mt: 1, mb: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0d9488',
                                  textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
                  🔀 Comisiones a cruzar contra la CxC
                </Typography>
                {comisionesPendientes.length === 0 ? (
                  <Alert severity="warning" sx={{ fontSize: 12 }}>
                    Esta agencia no tiene comisiones pendientes que Entretur le deba. Nada que cruzar.
                  </Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined"
                    sx={{ maxHeight: 220, overflowY: 'auto', flexShrink: 0, mb: 1 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ '& th': { bgcolor: '#f0fdfa', fontWeight: 700, fontSize: 11 } }}>
                          <TableCell padding="checkbox">
                            <input type="checkbox"
                              checked={comisionesPendientes.length > 0 && comisionesPendientes.every(c => comisionesSel[c.id])}
                              onChange={(e) => {
                                const all = e.target.checked;
                                const next = {};
                                comisionesPendientes.forEach(c => { next[c.id] = all; });
                                setComisionesSel(next);
                              }} />
                          </TableCell>
                          <TableCell sx={{ fontSize: 11 }}>Código</TableCell>
                          <TableCell sx={{ fontSize: 11 }}>Cliente</TableCell>
                          <TableCell align="right" sx={{ fontSize: 11 }}>Comisión pendiente</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {comisionesPendientes.map(c => (
                          <TableRow key={c.id} hover
                            onClick={() => setComisionesSel(s => ({ ...s, [c.id]: !s[c.id] }))}
                            sx={{ cursor: 'pointer',
                                  bgcolor: comisionesSel[c.id] ? '#f0fdfa' : 'inherit' }}>
                            <TableCell padding="checkbox">
                              <input type="checkbox"
                                checked={!!comisionesSel[c.id]}
                                onChange={() => setComisionesSel(s => ({ ...s, [c.id]: !s[c.id] }))} />
                            </TableCell>
                            <TableCell sx={{ fontSize: 11 }}>{c.codigo}</TableCell>
                            <TableCell sx={{ fontSize: 11 }}>{c.cliente}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700, color: '#0d9488' }}>
                              {fmtMoney(c.comision)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                           px: 1.5, py: 1, borderRadius: 1, bgcolor: '#f0fdfa', border: '1px solid #99f6e4' }}>
                  <Typography sx={{ fontSize: 11.5, color: '#134e4a', fontWeight: 600 }}>
                    Total comisión seleccionada · máximo a distribuir en CxC:
                  </Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0d9488' }}>
                    {fmtMoney(totalComisionSeleccionada)}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Distribución manual — con flexShrink:0 para que no la aplaste el flex column */}
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0891b2', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, mt: 1, flexShrink: 0 }}>
              Distribución del pago
            </Typography>
            {reservas.length === 0 ? (
              <Alert severity="success" sx={{ fontSize: 12, flexShrink: 0 }}>
                Esta agencia no tiene reservas con saldo pendiente. No hay CxC por cobrar.
              </Alert>
            ) : (
              // flexShrink: 0 protege esta tabla de ser aplastada por el flexbox
              // column del StyledDialog.Body cuando otros elementos crecen (ej.
              // aparece el resumen de retención o el bloque de errores). Sin
              // esto la grilla de reservas se encoge hasta casi desaparecer.
              <TableContainer component={Paper} variant="outlined"
                sx={{ maxHeight: 320, overflowX: 'auto', overflowY: 'auto', flexShrink: 0 }}>
                <Table size="small" stickyHeader sx={{ minWidth: 760 }}>
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 700, fontSize: 11 } }}>
                      <TableCell sx={{ minWidth: 130, whiteSpace: 'nowrap' }}>Código</TableCell>
                      <TableCell sx={{ minWidth: 160 }}>Cliente</TableCell>
                      <TableCell sx={{ minWidth: 90, whiteSpace: 'nowrap' }}>Salida</TableCell>
                      <TableCell align="right" sx={{ minWidth: 110, whiteSpace: 'nowrap' }}>Pendiente</TableCell>
                      <TableCell align="right" sx={{ minWidth: 220, whiteSpace: 'nowrap' }}>Monto a aplicar</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reservas.map(r => {
                      const saldo = Number(r.saldo_pendiente || 0);
                      const val = distribucion[r.id] ?? '';
                      return (
                        <TableRow key={r.id} hover>
                          <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>{r.codigo_reserva}</TableCell>
                          <TableCell sx={{ fontSize: 11 }}>{r.nombre_cliente}</TableCell>
                          <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtFecha(r.salida_fecha)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700, color: '#dc2626', whiteSpace: 'nowrap' }}>
                            {fmtMoney(saldo)}
                          </TableCell>
                          <TableCell align="right" sx={{ minWidth: 220 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'flex-end' }}>
                              <TextField size="small" type="number" value={val}
                                onChange={e => cambiarMonto(r.id, e.target.value)}
                                onFocus={e => e.target.select()}
                                inputProps={{ min: 0, max: saldo, step: 1000, style: { textAlign: 'right', fontSize: 13 } }}
                                sx={{ width: 150 }}
                                placeholder="0" />
                              <Tooltip title={`Aplicar todo el saldo (${fmtMoney(saldo)})`}>
                                <Button size="small" variant="outlined"
                                  onClick={() => asignarSaldoTotal(r.id, saldo)}
                                  sx={{
                                    minWidth: 50, textTransform: 'none', fontSize: 11, fontWeight: 700,
                                    borderColor: '#0891b2', color: '#0891b2',
                                    '&:hover': { borderColor: '#0e7490', bgcolor: '#ecfeff' },
                                  }}>
                                  Todo
                                </Button>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Resumen — desglose transferido vs retención */}
            <Box sx={{ mt: 1, p: 1.5, borderRadius: 2, bgcolor: '#f8fafc' }}>
              {retencionNum > 0 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                    <Typography sx={{ fontSize: 11, color: '#64748b' }}>Efectivo/transferido a la cuenta</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                      {fmtMoney(montoTransferido)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
                    <Typography sx={{ fontSize: 11, color: '#64748b' }}>Retención aplicada</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#a16207' }}>
                      + {fmtMoney(retencionNum)}
                    </Typography>
                  </Box>
                  <Box sx={{ borderTop: '1px dashed #cbd5e1', pt: 0.75 }} />
                </>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                  {retencionNum > 0
                    ? 'Total aplicado al saldo (transferido + retención)'
                    : 'Total distribuido — es lo que se registra como monto del pago'}
                </Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: totalDistribuido > 0 ? '#0891b2' : '#94a3b8' }}>
                  {fmtMoney(totalDistribuido)}
                </Typography>
              </Box>
            </Box>

            {errores.length > 0 && (
              <Alert severity="warning" sx={{ fontSize: 12, mt: 1 }}>
                <strong>Ajusta antes de guardar:</strong>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {errores.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </Alert>
            )}
            {err && <Alert severity="error" sx={{ fontSize: 12, mt: 1 }}>{err}</Alert>}
          </>
        )}
      </StyledDialog.Body>
      <StyledDialog.CompactActions>
        <StyledDialog.CancelButton fullWidth onClick={() => onClose(false)} disabled={saving} />
        <StyledDialog.PrimaryButton fullWidth onClick={handleGuardar} loading={saving} disabled={!puedeGuardar}>
          {esCruce ? '🔀 Aplicar cruce' : esRetencion ? '📋 Aplicar retenciones' : 'Registrar pago'}
        </StyledDialog.PrimaryButton>
      </StyledDialog.CompactActions>

    </StyledDialog>
  );
}

/**
 * TileCxC — calcado del Tile de VentasDiaPage.jsx. Fondo tintado, rótulo
 * diminuto en mayúsculas y valor en monoespaciada. Muestra la cifra
 * COMPLETA: en esta pantalla hay ancho de sobra y "$5.670.000" se lee de
 * una vez, mientras "$5,67M" obliga a traducir mentalmente.
 *
 * `conteo` para cantidades (van sin $) y `texto` para valores ya formateados
 * como "12 días".
 */
function TileCxC({ label, v, color, bg, conteo = false, texto = false }) {
  const valor = texto
    ? v
    : conteo
      ? new Intl.NumberFormat('es-CO').format(Number(v || 0))
      : `$${new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 }).format(Number(v || 0))}`;

  return (
    <Box sx={{
      p: { xs: 1, md: 1.25 }, bgcolor: bg, borderRadius: 1.5,
      height: '100%', display: 'flex', flexDirection: 'column',
      justifyContent: 'space-between', minWidth: 0, overflow: 'hidden', gap: 0.5,
    }}>
      <Typography sx={{
        fontSize: { xs: 9, sm: 9, lg: 8.5 },
        color, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 0.3, lineHeight: 1.2, alignSelf: 'flex-start',
        display: '-webkit-box', WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word',
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: { xs: 13, sm: 14, md: 14, lg: 15, xl: 16 },
        fontWeight: 800, color,
        fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
        lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden',
        textOverflow: 'ellipsis', fontVariantNumeric: 'tabular-nums',
      }}>
        {valor}
      </Typography>
    </Box>
  );
}
